/**
 * Purchase order detail — types and pure helpers.
 * Shell: PurchaseOrderDetail.tsx. Draft forms: @/lib/purchaseOrderDraftForm.
 */
import { HelpCircle } from "lucide-react";
import { purchaseOrderPaymentTermsDifferFromSupplier } from "@integrator-src/config/payment-terms-reference";
import { PurchaseInvoiceDetailLink } from "@/components/hub/HubEntityDetailLink";
import { HoverHintTrigger } from "@/components/ui/title-help";
import type { HubPurchaseOrderStatus, MrpPurchaseOrderEvent } from "@/lib/mrp";
import type { PurchaseOrderCurrency } from "@/lib/purchaseOrderDraftForm";

export interface SupplierForCompose {
  id: number;
  mv_supplier_id: number;
  mv_supplier_name: string | null;
  currency: string | null;
  payment_terms: string | null;
}

export function normalizePurchaseOrderMvStatus(status: string | null | undefined): string {
  return (status ?? "").trim().toLowerCase();
}

export function normalizeHubPurchaseOrderStatus(status: string | null | undefined): HubPurchaseOrderStatus | null {
  const normalized = normalizePurchaseOrderMvStatus(status);
  if (normalized === "draft" || normalized === "ordered" || normalized === "closed" || normalized === "cancelled") {
    return normalized;
  }
  if (normalized === "approved") return "ordered";
  if (normalized === "pending") return "draft";
  return null;
}

export function megaventoryLifecycleStatusForHubActions(hubStatus: HubPurchaseOrderStatus | null): string {
  if (hubStatus === "ordered") {
    return "verified";
  }
  return hubStatus ?? "";
}

export function purchaseOrderMvStatusBadgeTone(
  status: string | null | undefined,
): "neutral" | "success" | "warning" | "danger" | "info" | "muted" {
  const normalized = normalizePurchaseOrderMvStatus(status);
  if (normalized === "verified") return "success";
  if (normalized === "pending") return "info";
  if (normalized === "cancelled" || normalized === "canceled") return "danger";
  if (normalized === "closed" || normalized === "fullyinvoiced") return "muted";
  return "neutral";
}

export function formatPoActor(email: string | null | undefined): string {
  return email?.trim() ? email : "—";
}

export function formatPurchaseOrderEventPayloadValue(value: unknown): string {
  if (value == null) {
    return "—";
  }
  const text = String(value).trim();
  return text === "" ? "—" : text;
}

export function purchaseOrderEventChangeDetail(
  event: MrpPurchaseOrderEvent,
): { ariaLabel: string; body: string } | null {
  const payload = event.payload;
  if (payload == null) {
    return null;
  }
  const fromValue = payload.from_label ?? payload.from;
  const toValue = payload.to_label ?? payload.to;
  if (fromValue === undefined && toValue === undefined) {
    return null;
  }
  const from = formatPurchaseOrderEventPayloadValue(fromValue);
  const to = formatPurchaseOrderEventPayloadValue(toValue);
  const cpn = typeof payload.cpn === "string" ? payload.cpn.trim() : "";
  const lines: string[] = [];
  if (cpn !== "") {
    lines.push(cpn, "");
  }
  lines.push(`From: ${from}`, `To: ${to}`);
  return {
    ariaLabel: cpn !== "" ? `${cpn}: changed from ${from} to ${to}` : `Changed from ${from} to ${to}`,
    body: lines.join("\n"),
  };
}

export function purchaseInvoiceIdFromPurchaseOrderEvent(event: MrpPurchaseOrderEvent): number | null {
  const raw = event.payload?.purchase_invoice_id;
  const id = typeof raw === "number" ? raw : typeof raw === "string" ? Number.parseInt(raw, 10) : Number.NaN;
  if (!Number.isFinite(id) || id <= 0) return null;
  return id;
}

export function PurchaseOrderEventSummaryCell({
  event,
  gipiMvNumber,
}: {
  event: MrpPurchaseOrderEvent;
  gipiMvNumber?: string | null;
}): JSX.Element {
  const changeDetail = purchaseOrderEventChangeDetail(event);
  const purchaseInvoiceId = purchaseInvoiceIdFromPurchaseOrderEvent(event);
  return (
    <div className="inline-flex items-start gap-1">
      <span className="min-w-0">
        {purchaseInvoiceId == null ? (
          event.summary
        ) : (
          <PurchaseOrderGipiEventSummary
            summary={event.summary}
            purchaseInvoiceId={purchaseInvoiceId}
            mvNumber={gipiMvNumber}
          />
        )}
      </span>
      {changeDetail != null ? (
        <HoverHintTrigger
          className="shrink-0"
          trigger={
            <button
              type="button"
              tabIndex={-1}
              className="inline-flex shrink-0 rounded p-0.5 text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-1"
              aria-label={changeDetail.ariaLabel}
            >
              <HelpCircle className="h-4 w-4 shrink-0" aria-hidden />
            </button>
          }
        >
          <span className="block max-w-sm whitespace-pre-wrap break-words">{changeDetail.body}</span>
        </HoverHintTrigger>
      ) : null}
    </div>
  );
}

export const PURCHASE_ORDER_EVENT_TYPE_LABEL: Record<MrpPurchaseOrderEvent["event_type"], string> = {
  hub_created: "Created in hub",
  lifecycle_verify: "Approved",
  lifecycle_revert_to_pending: "Reverted to pending",
  lifecycle_cancel: "Cancelled",
  hub_closed: "Closed",
  hub_finalized: "Finalized",
  hub_approved: "Approved",
  hub_reverted_to_pending: "Reverted to pending",
  hub_cancelled: "Cancelled",
  hub_cutover_migrated: "Migrated to hub",
  hub_receipt_submitted: "Receipt submitted",
  hub_receipt_voided: "Receipt voided",
  hub_invoice_submitted: "Invoice submitted",
  hub_gipi_created: "GIPI created",
  hub_gipi_failed: "GIPI failed",
  digikey_order_submitted: "DigiKey order submitted",
  expected_date_set: "Expected date",
  expected_date_all_lines_set: "Expected date (all lines)",
  delivery_split_set: "Delivery split",
  hub_comment: "Comment",
  payment_terms_set: "Payment terms",
  special_instructions_set: "Special instructions",
  amount_rounding_mode_set: "Amount rounding",
  line_description_set: "Line description",
  line_unit_price_set: "Unit price",
  line_quantity_set: "Quantity",
  line_split: "Line split",
  line_added: "Line added",
  line_removed: "Line removed",
};

function PurchaseOrderGipiEventSummary({
  summary,
  purchaseInvoiceId,
  mvNumber,
}: {
  summary: string;
  purchaseInvoiceId: number;
  mvNumber?: string | null;
}): JSX.Element {
  const label = mvNumber?.trim() ?? "";
  if (label !== "" && summary.includes(label)) {
    const index = summary.indexOf(label);
    return (
      <>
        {summary.slice(0, index)}
        <PurchaseInvoiceDetailLink purchaseInvoiceId={purchaseInvoiceId} mvNumber={label} />
        {summary.slice(index + label.length)}
      </>
    );
  }
  const remainder = summary.replace(/^(Direct )?GIPI created\s*/i, "").trim();
  return (
    <>
      <PurchaseInvoiceDetailLink purchaseInvoiceId={purchaseInvoiceId} mvNumber={label === "" ? null : label} />
      {remainder === "" ? null : ` ${remainder}`}
    </>
  );
}

export function purchaseOrderPaymentTermsDiffersFromSupplier(
  paymentTerms: string | null | undefined,
  supplierPaymentTerms: string | null | undefined,
  enumerationToLabel: Record<string, string> = {},
): boolean {
  return purchaseOrderPaymentTermsDifferFromSupplier(paymentTerms, supplierPaymentTerms, enumerationToLabel);
}

export function parsePositiveSupplierId(raw: string | number | null | undefined): number | null {
  if (raw == null || raw === "") {
    return null;
  }
  const parsed = typeof raw === "number" ? raw : Number.parseInt(String(raw), 10);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null;
  }
  return parsed;
}

export type HubDraftPersistField = "supplier" | "location";

export type HubDraftPersistInput = {
  locationCode: string;
  locationId?: number | null;
  supplierId: number | null;
  currency?: PurchaseOrderCurrency | null;
  statusFields: HubDraftPersistField[];
};
